import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Switch, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {resolveExtensionIconSrc} from 'dev-platform/extensionIconMapper';
import ContentStoreModal from 'extensions/content-store/ContentStoreModal';
import {AgentType, ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {AgentApprovalUpdateSource, CitationGenerationMode} from 'llm/chat-library/ChatDAO';
import {AISettingsTab, openSettings} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {
  doesAgentTypeSupportPublicData,
  doesAgentTypeSupportSources,
  doesAssistantDeprecateAgent,
  getAgentDescriptionForChatInput,
  getAgentTypeText,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import UploadSkillModal from 'llm/chat-library/skills/UploadSkillModal';
import useSkills from 'llm/chat-library/skills/useSkills';
import AIButton from 'llm/chat-library/ui/AIButton';
import AIMenu from 'llm/chat-library/ui/AIMenu';
import FullActionsInfoPopover from 'llm/chat-library/ui/FullActionsInfoPopover';
import {isMcpAiConnectorsFeatureEnabled} from 'llm/isMcpAiConnectorsFeatureEnabled';
import {
  type DisabledServer,
  type EnabledServer,
  useMCPEnabledServersOptional,
} from 'llm/mcp-client-library/MCPEnabledServersContext';
import {MCP_PRODUCT_NAME_PLURAL} from 'llm/NamingConstants';
import ConnectMCPModal from 'nav/sidepanel/mcps/ConnectMCPModal';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import {useMcpDirectoryInstallHandler} from 'tenants/admin/extensions/mcp/useMcpDirectoryInstallHandler';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import HelpIcon from 'ui-library/icons/HelpIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuSelectedIconPlacement, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

const PLUS_MENU_NO_SELECTION = '__plus_menu_no_selection__';

function McpServerIcon({server}: {server: EnabledServer | DisabledServer}): JSX.Element {
  if (server.iconUrl != null) {
    return (
      <img
        src={resolveExtensionIconSrc(server.iconUrl)}
        alt=""
        className={css(styles.serverIconImg)}
        {...getTestProps({component: 'PlusMenu', element: 'mcp-server-icon', key: server.id})}
      />
    );
  }
  // TODO(BNCH-151518): fall back to the icon from the MCP `initialize` response.
  return <BenchlingIcon icon="sources" />;
}

export default function PlusMenu({
  fileInputRef,
  agentType,
  availableAgentTypes,
  setAgentType,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  citationGenerationMode,
  setCitationGenerationMode,
  fullAccessEnabled,
  onGrantFullAccess,
  onRevokeFullAccess,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
  onSelectSkill,
  useBackgroundAgentQueue,
  setUseBackgroundAgentQueue,
}: {
  fileInputRef: React.RefObject<HTMLInputElement>;
  agentType: AgentType | null;
  availableAgentTypes?: Array<AgentType>;
  setAgentType?: (agentType: AgentType | null) => void;
  inputDisabled: boolean;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (enabled: boolean) => void;
  citationGenerationMode: CitationGenerationMode;
  setCitationGenerationMode: (mode: CitationGenerationMode) => void;
  fullAccessEnabled: boolean;
  onGrantFullAccess?: (source: AgentApprovalUpdateSource) => Promise<boolean>;
  onRevokeFullAccess?: (source: AgentApprovalUpdateSource) => Promise<boolean>;
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
  onSelectSkill?: (skillName: string) => void;
  /**
   * Background-agent-queue toggle for AI Scientist chats, gated by LLM_ENABLE_LEGACY_DATA_IMPORT_AGENT_QUEUE.
   * It is only meant to be available to internal admin users. It is only passed for new chats. If undefined,
   * the switch is not shown and the chat is routed through the normal assistant queue.
   */
  useBackgroundAgentQueue?: boolean;
  setUseBackgroundAgentQueue?: (enabled: boolean) => void;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredDisabledServerId, setHoveredDisabledServerId] = useState<string | null>(null);
  const onMcpDirectoryInstall = useMcpDirectoryInstallHandler();
  const isOpenAIEnabled = BenchlingConfig.LLM_ENABLE_OPENAI;
  const isAWSWebSearchEnabled = BenchlingConfig.LLM_ENABLE_AWS_WEB_SEARCH;
  const mcpEnabledServers = useMCPEnabledServersOptional();
  const isMcpServersLoading = mcpEnabledServers?.isLoading ?? false;
  const hasTenantMcpConfigs = mcpEnabledServers?.hasTenantMcpConfigs ?? false;
  const enabledServersList = mcpEnabledServers?.enabledServers;
  const disabledServersList = mcpEnabledServers?.disabledServers;
  const enabledServers = useMemo(() => enabledServersList ?? [], [enabledServersList]);
  const disabledServers = useMemo(() => disabledServersList ?? [], [disabledServersList]);
  const {llmEnableCitationsToggle} = useRolloutFlags();
  const citationsEnabled = citationGenerationMode !== CitationGenerationMode.DISABLED;
  const isCitationGenerationRequired = citationGenerationMode === CitationGenerationMode.REQUIRED;

  const showPublicData = agentType != null && doesAgentTypeSupportPublicData(agentType);
  const showCitationsToggle = llmEnableCitationsToggle === true && agentType === AgentType.ASSISTANT;
  const showModeSection = BenchlingConfig.LLM_AGENT_APPROVALS_RELEASED;
  const isFullAccessLocked = showModeSection && !BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS;
  const selectedAccessMode = fullAccessEnabled ? 'full-access' : 'default-access';
  const showLegacyDataImportToggle =
    agentType === AgentType.CO_SCIENTIST &&
    BenchlingConfig.LLM_ENABLE_LEGACY_DATA_IMPORT_AGENT_QUEUE &&
    setUseBackgroundAgentQueue != null;
  const showMcpSection =
    agentType != null &&
    doesAgentTypeSupportSources(agentType) &&
    setSelectedMcpServerIds != null &&
    isMcpAiConnectorsFeatureEnabled();
  const hasCoScientist =
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT &&
    availableAgentTypes?.includes(AgentType.CO_SCIENTIST) === true;
  const legacyAgents = useMemo(
    () =>
      BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT && availableAgentTypes != null
        ? availableAgentTypes.filter((agentOption) => doesAssistantDeprecateAgent(agentOption))
        : [],
    [availableAgentTypes]
  );
  const showAssistantAgentSection =
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT &&
    setAgentType != null &&
    (hasCoScientist || legacyAgents.length > 0);
  const selectedAssistantMenuAgentValue =
    showAssistantAgentSection &&
    agentType != null &&
    (agentType === AgentType.CO_SCIENTIST || legacyAgents.includes(agentType))
      ? agentType
      : PLUS_MENU_NO_SELECTION;

  const effectiveSelectedIds = selectedMcpServerIds ?? enabledServers.map((s) => s.id);

  const handleToggleServer = useCallback(
    (serverId: string): void => {
      if (!setSelectedMcpServerIds) {
        return;
      }
      const isCurrentlySelected = effectiveSelectedIds.includes(serverId);
      if (isCurrentlySelected) {
        setSelectedMcpServerIds(effectiveSelectedIds.filter((id) => id !== serverId));
      } else {
        const newIds = [...effectiveSelectedIds, serverId];
        if (newIds.length === enabledServers.length) {
          setSelectedMcpServerIds(undefined);
        } else {
          setSelectedMcpServerIds(newIds);
        }
      }
    },
    [effectiveSelectedIds, enabledServers, setSelectedMcpServerIds]
  );

  const handleAttachClick = useCallback((): void => {
    if (!inputDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [inputDisabled, fileInputRef]);

  const handleManageMCPs = useCallback((): void => {
    openSettings(AISettingsTab.AI_CONNECTORS);
  }, []);

  const showSkillsSection = agentType != null && onSelectSkill != null;

  const {skills, createNewSkills} = useSkills({enabled: showSkillsSection});
  const enabledSkills = useMemo(() => skills.filter((skill) => skill.isEnabled), [skills]);

  const handleManageSkills = useCallback(() => {
    openSettings(AISettingsTab.SKILLS);
  }, []);

  const handleAddSkill = useCallback((): void => {
    legacyRunModal(UploadSkillModal, {
      onRequestSubmit: createNewSkills,
    });
  }, [createNewSkills]);

  const handleBrowseMcpDirectory = useCallback((): void => {
    legacyRunModal(ContentStoreModal, {
      extensionType: ExtensionType.MCP_SERVER,
      onInstall: onMcpDirectoryInstall,
    });
  }, [onMcpDirectoryInstall]);

  const closeMenu = useCallback((): void => {
    setIsOpen(false);
    setHoveredDisabledServerId(null);
  }, []);

  const handleConnectMcpServer = useCallback(
    (server: {id: string; name: string; authType: string}): void => {
      legacyRunModal(ConnectMCPModal, {
        config: {
          id: server.id,
          name: server.name,
          authType: server.authType,
        },
        onRequestSubmit: () => {
          mcpEnabledServers?.refetch();
        },
      });
    },
    [mcpEnabledServers]
  );

  const menu = useMemo(
    () => (
      <AIMenu
        scrollable={false}
        height={MenuHeight.SMALL}
        width={MenuWidth.XLARGE}
        value={selectedAssistantMenuAgentValue}
        useSelectedIconIfSingleSelect={true}
        selectedIconPlacement={MenuSelectedIconPlacement.TRAILING}
      >
        <Section>
          <Option
            contentClassName={css(styles.menuOptionContent)}
            childrenClassName={css(styles.menuOptionChildren)}
            value="attach-files"
            icon="paperclip"
            isDisabled={inputDisabled}
            onSelect={() => {
              handleAttachClick();
              closeMenu();
            }}
          >
            Add photos and files
          </Option>
          {showPublicData && (
            <Option
              contentClassName={css(styles.menuOptionContent)}
              childrenClassName={css(styles.menuOptionChildren)}
              value="public-data"
              icon="globe"
              dataTestAttrs={{component: 'PlusMenu', element: 'publicDataOption'}}
              isDisabled={!(isOpenAIEnabled || isAWSWebSearchEnabled)}
              onSelect={() => {
                if (isOpenAIEnabled || isAWSWebSearchEnabled) {
                  setPublicDataEnabled(!publicDataEnabled);
                }
              }}
            >
              <span className={css(styles.toggleRow)}>
                <span>{isAWSWebSearchEnabled ? 'Web search' : 'Public data'}</span>
                <Switch
                  checked={publicDataEnabled}
                  disabled={!(isOpenAIEnabled || isAWSWebSearchEnabled)}
                  onChange={() => {}}
                  size="xs"
                  styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
                />
              </span>
            </Option>
          )}
          {showCitationsToggle && (
            <Option
              contentClassName={css(styles.menuOptionContent)}
              childrenClassName={css(styles.menuOptionChildren)}
              value="citations"
              icon="superscript"
              isDisabled={isCitationGenerationRequired}
              onSelect={() => {
                if (isCitationGenerationRequired) {
                  return;
                }
                setCitationGenerationMode(
                  citationsEnabled ? CitationGenerationMode.DISABLED : CitationGenerationMode.AUTO
                );
              }}
            >
              <span className={css(styles.toggleRow)}>
                <span>Citations</span>
                <Switch
                  checked={citationsEnabled}
                  readOnly={true}
                  tabIndex={-1}
                  onChange={() => {}}
                  size="xs"
                  styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
                />
              </span>
            </Option>
          )}
          {showLegacyDataImportToggle && (
            <Option
              contentClassName={css(styles.menuOptionContent)}
              childrenClassName={css(styles.menuOptionChildren)}
              value="legacy-data-import"
              icon="database"
              dataTestAttrs={{component: 'PlusMenu', element: 'legacyDataImportOption'}}
              isDisabled={inputDisabled}
              onSelect={() => {
                if (inputDisabled) {
                  return;
                }
                setUseBackgroundAgentQueue(!useBackgroundAgentQueue);
              }}
            >
              <span className={css(styles.toggleRow)}>
                <span>Legacy data import</span>
                <Switch
                  checked={Boolean(useBackgroundAgentQueue)}
                  disabled={inputDisabled}
                  onChange={() => {}}
                  size="xs"
                  styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
                />
              </span>
            </Option>
          )}
        </Section>
        {showAssistantAgentSection && (
          <Section>
            {hasCoScientist && (
              <Option
                contentClassName={css(styles.menuOptionContent)}
                childrenClassName={css(styles.menuOptionChildren)}
                value={AgentType.CO_SCIENTIST}
                icon="flask"
                onSelect={() => {
                  setAgentType?.(
                    agentType === AgentType.CO_SCIENTIST ? AgentType.ASSISTANT : AgentType.CO_SCIENTIST
                  );
                  closeMenu();
                }}
                metadata={
                  getAgentDescriptionForChatInput(AgentType.CO_SCIENTIST) != null ? (
                    <span className={css(styles.agentMenuOptionSubtitle)}>
                      {getAgentDescriptionForChatInput(AgentType.CO_SCIENTIST)}
                    </span>
                  ) : null
                }
              >
                {getAgentTypeText(AgentType.CO_SCIENTIST)}
              </Option>
            )}
            {legacyAgents.length > 0 && (
              <Option
                contentClassName={css(styles.menuOptionContent)}
                childrenClassName={css(styles.menuOptionChildren)}
                value="agent-legacy-submenu"
                icon="sparkles"
              >
                Legacy agents
                <Menu
                  width={MenuWidth.LARGE}
                  value={selectedAssistantMenuAgentValue}
                  useSelectedIconIfSingleSelect={true}
                  selectedIconPlacement={MenuSelectedIconPlacement.TRAILING}
                >
                  {legacyAgents.map((agentOption) => (
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      key={agentOption}
                      value={agentOption}
                      onSelect={() => {
                        setAgentType?.(agentType === agentOption ? AgentType.ASSISTANT : agentOption);
                        closeMenu();
                      }}
                      metadata={
                        getAgentDescriptionForChatInput(agentOption) != null ? (
                          <span className={css(styles.agentMenuOptionSubtitle)}>
                            {getAgentDescriptionForChatInput(agentOption)}
                          </span>
                        ) : null
                      }
                    >
                      {getAgentTypeText(agentOption)}
                    </Option>
                  ))}
                </Menu>
              </Option>
            )}
          </Section>
        )}
        {(showMcpSection || showSkillsSection || showModeSection) && (
          <Section>
            {showMcpSection && (
              <Option
                contentClassName={css(styles.menuOptionContent)}
                childrenClassName={css(styles.menuOptionChildren)}
                value="mcp-servers-submenu"
                icon="sources"
              >
                {MCP_PRODUCT_NAME_PLURAL}
                <Menu width={MenuWidth.MEDIUM}>
                  {isMcpServersLoading ? (
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      value="mcp-loading"
                      isDisabled={true}
                    >
                      Loading...
                    </Option>
                  ) : enabledServers.length === 0 && disabledServers.length === 0 ? (
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      value="mcp-none"
                      isDisabled={true}
                    >
                      {hasTenantMcpConfigs
                        ? `${MCP_PRODUCT_NAME_PLURAL} are available, but must be connected first.`
                        : `Ask your admin to make ${MCP_PRODUCT_NAME_PLURAL} available for your workspace.`}
                    </Option>
                  ) : (
                    enabledServers.map((server) => (
                      <Option
                        contentClassName={css(styles.menuOptionContent)}
                        childrenClassName={css(styles.menuOptionChildren)}
                        avatarClassName="menu-option-iconWrapper"
                        key={server.id}
                        value={`mcp-${server.id}`}
                        avatar={<McpServerIcon server={server} />}
                        onSelect={() => {
                          handleToggleServer(server.id);
                        }}
                      >
                        <span className={css(styles.toggleRow)}>
                          <span>{server.name}</span>
                          <Switch
                            checked={effectiveSelectedIds.includes(server.id)}
                            onChange={() => {}}
                            size="xs"
                            styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
                          />
                        </span>
                      </Option>
                    ))
                  )}
                  {disabledServers.length > 0 && (
                    <Section>
                      {disabledServers.map((server) => {
                        const isHovered = hoveredDisabledServerId === server.id;
                        return (
                          <Option
                            key={`mcp-disabled-${server.id}`}
                            contentClassName={css(styles.menuOptionContent)}
                            childrenClassName={css(styles.menuOptionChildren)}
                            value={`mcp-disabled-${server.id}`}
                            avatar={<McpServerIcon server={server} />}
                            avatarClassName="menu-option-iconWrapper"
                            onHover={() => {
                              setHoveredDisabledServerId(server.id);
                            }}
                            onMouseOut={() => {
                              setHoveredDisabledServerId((prevId) => (prevId === server.id ? null : prevId));
                            }}
                            onSelect={() => {
                              closeMenu();
                              handleConnectMcpServer(server);
                            }}
                          >
                            <span className={css(styles.connectOptionRow)}>
                              <span className={css(styles.connectOptionName)}>{server.name}</span>
                              <span
                                className={css(
                                  styles.connectOptionAction,
                                  isHovered && styles.connectOptionActionVisible
                                )}
                              >
                                Connect
                              </span>
                            </span>
                          </Option>
                        );
                      })}
                    </Section>
                  )}
                  {hasTenantMcpConfigs && (
                    <Section>
                      <Option
                        contentClassName={css(styles.menuOptionContent)}
                        childrenClassName={css(styles.menuOptionChildren)}
                        value="manage-mcps"
                        icon="gear"
                        onSelect={() => {
                          closeMenu();
                          handleManageMCPs();
                        }}
                      >
                        Manage connectors
                      </Option>
                    </Section>
                  )}
                  {BenchlingConfig.ENABLE_EXTENSION_DIRECTORY && (
                    <Section>
                      <Option
                        contentClassName={css(styles.menuOptionContent)}
                        childrenClassName={css(styles.menuOptionChildren)}
                        value="browse-mcp-directory"
                        icon="store"
                        onSelect={() => {
                          closeMenu();
                          handleBrowseMcpDirectory();
                        }}
                      >
                        Browse directory
                      </Option>
                    </Section>
                  )}
                </Menu>
              </Option>
            )}
            {showSkillsSection && (
              <Option
                contentClassName={css(styles.menuOptionContent)}
                childrenClassName={css(styles.menuOptionChildren)}
                value="skills-submenu"
                icon="file-lines"
                dataTestAttrs={{component: 'PlusMenu', element: 'skillsSubmenuTrigger'}}
              >
                Skills
                <Menu width={MenuWidth.MEDIUM}>
                  <Section>
                    {enabledSkills.map((skill) => (
                      <Option
                        contentClassName={css(styles.menuOptionContent)}
                        childrenClassName={css(styles.menuOptionChildren)}
                        key={skill.id}
                        value={`skill-${skill.id}`}
                        avatar={
                          <span
                            aria-hidden="true"
                            {...getTestProps({
                              component: 'PlusMenu',
                              element: 'skillIconSpacer',
                              key: skill.id,
                            })}
                          />
                        }
                        avatarClassName="menu-option-iconWrapper"
                        onSelect={() => {
                          trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_SKILL_FROM_PLUS_MENU, {
                            skillId: skill.id,
                            agentType,
                          });
                          onSelectSkill(skill.name);
                          closeMenu();
                        }}
                        dataTestAttrs={{component: 'PlusMenu', element: 'skillItem', key: skill.id}}
                      >
                        {skill.name}
                      </Option>
                    ))}
                    {enabledSkills.length === 0 && (
                      <Option
                        contentClassName={css(styles.menuOptionContent)}
                        childrenClassName={css(styles.menuOptionChildren)}
                        value="no-skills"
                        avatar={<span aria-hidden="true" />}
                        avatarClassName="menu-option-iconWrapper"
                        isDisabled={true}
                      >
                        No skills available
                      </Option>
                    )}
                  </Section>
                  <Section>
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      value="manage-skills"
                      icon="settings"
                      onSelect={() => {
                        closeMenu();
                        handleManageSkills();
                      }}
                    >
                      Manage skills...
                    </Option>
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      value="add-skill"
                      icon="plus"
                      onSelect={() => {
                        closeMenu();
                        handleAddSkill();
                      }}
                    >
                      Add skill...
                    </Option>
                  </Section>
                </Menu>
              </Option>
            )}
            {showModeSection && (
              <Option
                contentClassName={css(styles.menuOptionContent)}
                childrenClassName={css(styles.menuOptionChildren)}
                value="mode-submenu"
                icon="sliders"
                dataTestAttrs={{component: 'PlusMenu', element: 'modeSubmenuTrigger'}}
              >
                Mode
                <Menu
                  width={MenuWidth.MEDIUM}
                  value={selectedAccessMode}
                  useSelectedIconIfSingleSelect={true}
                  selectedIconPlacement={MenuSelectedIconPlacement.TRAILING}
                >
                  <Section>
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      value="default-access"
                      metadata={
                        <span className={css(styles.agentMenuOptionSubtitle)}>Research and plan</span>
                      }
                      onSelect={async () => {
                        if (fullAccessEnabled) {
                          await onRevokeFullAccess?.(AgentApprovalUpdateSource.PLUS_MENU_MODE_SUBMENU);
                        }
                        closeMenu();
                      }}
                      dataTestAttrs={{component: 'PlusMenu', element: 'defaultAccessOption'}}
                    >
                      <span className={css(styles.accessOptionRow)}>Default</span>
                    </Option>
                    <Option
                      contentClassName={css(styles.menuOptionContent)}
                      childrenClassName={css(styles.menuOptionChildren)}
                      value="full-access"
                      isDisabled={isFullAccessLocked}
                      onSelect={async () => {
                        if (!fullAccessEnabled && !isFullAccessLocked) {
                          await onGrantFullAccess?.(AgentApprovalUpdateSource.PLUS_MENU_MODE_SUBMENU);
                        }
                        closeMenu();
                      }}
                      dataTestAttrs={{component: 'PlusMenu', element: 'fullAccessOption'}}
                    >
                      <span className={css(styles.accessOptionRow)}>
                        <span className={css(styles.accessOptionTextColumn)}>
                          <span className={css(styles.fullActionsLabelRow)}>
                            Full actions
                            <FullActionsInfoPopover>
                              <span
                                {...getTestProps({component: 'PlusMenu', element: 'fullActionsInfoIcon'})}
                              >
                                <HelpIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              </span>
                            </FullActionsInfoPopover>
                          </span>
                          <span
                            className={css(
                              styles.agentMenuOptionSubtitle,
                              isFullAccessLocked && styles.agentMenuOptionSubtitleDisabled
                            )}
                          >
                            Create and edit
                          </span>
                        </span>
                        {isFullAccessLocked && (
                          <Tooltip
                            multiline={true}
                            width={220}
                            withinPortal={true}
                            zIndex={ZINDEX_FLOATING_UI}
                            label="An admin needs to grant access before this mode can be used"
                          >
                            <span {...getTestProps({component: 'PlusMenu', element: 'fullActionsLockIcon'})}>
                              <BenchlingIcon aria-hidden="true" icon="lock" />
                            </span>
                          </Tooltip>
                        )}
                      </span>
                    </Option>
                  </Section>
                </Menu>
              </Option>
            )}
          </Section>
        )}
      </AIMenu>
    ),
    [
      closeMenu,
      disabledServers,
      effectiveSelectedIds,
      enabledServers,
      handleAddSkill,
      handleConnectMcpServer,
      handleManageSkills,
      hasTenantMcpConfigs,
      handleAttachClick,
      handleBrowseMcpDirectory,
      handleManageMCPs,
      handleToggleServer,
      isMcpServersLoading,
      hoveredDisabledServerId,
      inputDisabled,
      isAWSWebSearchEnabled,
      isOpenAIEnabled,
      onSelectSkill,
      citationsEnabled,
      isCitationGenerationRequired,
      setCitationGenerationMode,
      fullAccessEnabled,
      isFullAccessLocked,
      onGrantFullAccess,
      onRevokeFullAccess,
      publicDataEnabled,
      setPublicDataEnabled,
      showMcpSection,
      showAssistantAgentSection,
      showPublicData,
      showCitationsToggle,
      showModeSection,
      showLegacyDataImportToggle,
      hasCoScientist,
      legacyAgents,
      selectedAssistantMenuAgentValue,
      selectedAccessMode,
      agentType,
      setAgentType,
      showSkillsSection,
      enabledSkills,
      useBackgroundAgentQueue,
      setUseBackgroundAgentQueue,
    ]
  );

  return (
    <Dropdown
      menu={menu}
      isOpen={isOpen}
      onToggle={setIsOpen}
      closeOnInsideClick={false}
      closeOnOutsideClick={true}
      allowMenuToCloseDropdown={false}
    >
      <AIButton
        ariaLabel="More options"
        className="assistant-ai-chat-plus-button"
        disabled={inputDisabled}
        icon="plus"
      />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  accessOptionRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  accessOptionTextColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  fullActionsLabelRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
  },
  connectOptionAction: {
    color: Color.BLUE_500,
    fontSize: '12px',
    fontWeight: 600,
    opacity: 0,
    transition: 'opacity 100ms ease-in-out',
    flex: 0,
    display: 'none',
  },
  connectOptionActionVisible: {
    opacity: 1,
    display: 'block',
  },
  connectOptionRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
    justifyContent: 'space-between',
    width: '100%',
    // connectOptionName will ellipsize, so we don't need to wrap the text.
    wordBreak: 'normal',
  },
  connectOptionName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuOptionContent: {
    gap: 6,
    padding: '0 14px 0 20px',
  },
  menuOptionChildren: {
    flex: 1,
    minWidth: 0,
  },
  serverIconImg: {
    width: 16,
    height: 16,
    objectFit: 'contain',
  },
  agentMenuOptionSubtitle: {
    fontSize: '12px',
    color: Color.GRAY_700,
  },
  agentMenuOptionSubtitleDisabled: {
    // Matches the disabled-option title color (`.menu-option.is-disabled`) so the subtitle grays out
    // in step with the title when Full actions is locked.
    color: Color.GRAY_500,
  },
  toggleRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    width: '100%',
    minWidth: 0,
  },
});
